import { useEffect, useRef, useState } from "react";
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import { CustomAppBar, BackButton, HelpButton } from "../components";
import { FirstResultDialog, TutorialDialog } from "../dialogs";
import { nameToScaleMap } from "@/scales";
import { useScaleStore } from "../states/formState";
import { useAuthService } from "@/services/authService";
import { useTutorialService } from "@/services/tutorialService";
import { useTranslatedMessages } from "@/multiLanguage/i18n";
import { ClientData } from "@/services/pharmacyService";
import { ErrorDialog } from "@/dialogs/ErrorDialog";
import { FormDataFree, FreeScaleHookForm } from "@/scales/reactHookFreeScale";
import {
    sumbitHookFormEmo,
    sumbitHookFormFree,
    sumbitHookFormGlycated,
    sumbitHookFormLopre,
    sumbitHookFormUrine,
    sumbitHookFormUrineEmo,
} from "@/scales/scalesUtils/utils";
import { useLocalStorageService } from "@/states/localStorageState";
import { Network } from "@/services/network";
import { FormDataLopre, LopreScaleHookForm } from "@/scales/reactHookLopreScale";
import { FormDataGlycated, GlycatedScaleHookForm } from "@/scales/reactHookGlycatedScale";
import { FormDataUrine, UrineScaleHookForm } from "@/scales/reactHookUrineScale";
import { EmoglobinaScaleHookForm, FormDataEmo } from "@/scales/reactHookEmoglobinaScale";
import { FormDataUrineEmo, UrineEmoScaleHookForm } from "@/scales/reactHookUrineEmoScale";
import { constants } from "@/utils/constants";
import DevRiskCaseSelector from "@/components/dev/DevRiskCaseSelector";
import { freeScaleRiskCases } from "@/scales/scalesUtils/freeScaleRiskCases";
import { lopreScaleRiskCases } from "@/scales/scalesUtils/lopreScaleRiskCases";
import { glycatedScaleRiskCases } from "@/scales/scalesUtils/glycatedScaleRiskCases";

export const sendData = async (body: any) => {
    const response = await Network.sendScaleData(body);
    return response;
};

export default function ScalePage() {
    const lang = useTranslatedMessages();

    const countries = lang.countries;
    const user = useLocalStorageService((state) => state.user);
    const hasInitialized = useRef(false);
    const { palette } = useTheme();
    const location = useLocation();
    const selectedClient: ClientData = location.state ? location.state?.client : null;
    //TODO: è necessario usare zustand per memorizzare questi dati? perché non usestate e basta?

    const navigate = useNavigate();

    const { isLoggedIn } = useAuthService();
    const setSelectedScale = useScaleStore((state) => state.setSelectedScale);

    const [error, setError] = useState<string>("");
    const [reportId, setReportId] = useState<string | null>(null);
    const [reportPrivateKey, setreportPrivateKey] = useState<string | null>(null);

    const breakpoints = useTheme().breakpoints;
    const isXs = useMediaQuery(breakpoints.only("xs"));
    const isLg = useMediaQuery(breakpoints.up("lg"));
    const [openDialogs, setOpenDialogs] = useState({
        firstResult: false,
        errorDialog: false,
        tutorial: false,
    });
    const { isTutorialSeen, markTutorialAsSeen } = useTutorialService();

    const [scaleIndexes, setScaleIndexes] = useState<any>(null);
    const [prefillData, setPrefillData] = useState<any>(undefined);
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
        }
    }, [hasInitialized.current]);

    function switchVisibilityDialog(name: string) {
        setOpenDialogs((prevOpenDialogs) => ({
            ...prevOpenDialogs,
            [name]: !prevOpenDialogs[name],
        }));
    }

    const openTutorial = () => {
        markTutorialAsSeen();
        switchVisibilityDialog("tutorial");
    };

    let { scaleName } = useParams();

    // Dev: cambiando scala, azzera l'eventuale precompilamento per non mischiare i dati.
    useEffect(() => {
        setPrefillData(undefined);
    }, [scaleName]);

    const isScaleAvailable = () => {
        switch (scaleName as keyof typeof nameToScaleMap) {
            case "lopreHolder":
                return isLoggedIn();
            case "scaleGlycated":
                return isLoggedIn();
            case "scaleUrine":
                return isLoggedIn();
            case "scaleEmo":
                return isLoggedIn();
            case "scaleUrineEmo":
                return isLoggedIn();
            case "scaleFree":
                return true;
            default:
                return false;
        }
    };

    if (!isScaleAvailable()) {
        setSelectedScale(null);
        return <Navigate to={"/"} />;
    }

    const returnTypeScaleSubmit = (data: FormDataLopre | FormDataGlycated | FormDataFree | FormDataUrine | FormDataUrineEmo) => {
        switch (scaleName as keyof typeof nameToScaleMap) {
            case "lopreHolder":
                const lopreData = data as FormDataLopre;
                return sumbitHookFormLopre(lopreData, countries, user, selectedClient, lang);
            case "scaleGlycated":
                const glycatedData = data as FormDataGlycated;
                return sumbitHookFormGlycated(glycatedData, countries, user, selectedClient, lang);
            case "scaleUrine":
                const urineData = data as FormDataUrine;
                return sumbitHookFormUrine(urineData, countries, user, selectedClient, lang);
            case "scaleEmo":
                const emoData = data as FormDataEmo;
                return sumbitHookFormEmo(emoData, countries, user, selectedClient, lang);
            case "scaleUrineEmo":
                const urineEmoData = data as FormDataUrineEmo;
                return sumbitHookFormUrineEmo(urineEmoData, countries, user, selectedClient, lang);
            case "scaleFree":
                const freeData = data as FormDataFree;
                return sumbitHookFormFree(freeData, countries, user, selectedClient, lang);
            default:
                return false;
        }
    };
    const returnFormComponet = () => {
        switch (scaleName as keyof typeof nameToScaleMap) {
            case "lopreHolder":
                return (
                    <div
                        style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "20px" }}
                        data-testid="lopre-form-component"
                    >
                        {constants.IS_DEBUG && (
                            <DevRiskCaseSelector
                                cases={lopreScaleRiskCases}
                                onSelect={(data) => setPrefillData(data)}
                            />
                        )}
                        <LopreScaleHookForm
                            selectedClient={selectedClient}
                            onSubmit={sumbitHookForm}
                            prefillData={prefillData}
                        ></LopreScaleHookForm>
                    </div>
                );
            case "scaleGlycated":
                return (
                    <div
                        style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "20px" }}
                        data-testid="glycated-form-component"
                    >
                        {constants.IS_DEBUG && (
                            <DevRiskCaseSelector
                                cases={glycatedScaleRiskCases}
                                onSelect={(data) => setPrefillData(data)}
                            />
                        )}
                        <GlycatedScaleHookForm
                            selectedClient={selectedClient}
                            onSubmit={sumbitHookForm}
                            prefillData={prefillData}
                        ></GlycatedScaleHookForm>
                    </div>
                );
            case "scaleUrine":
                return (
                    <div
                        style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "20px" }}
                        data-testid="glycated-form-component"
                    >
                        <UrineScaleHookForm
                            selectedClient={selectedClient}
                            onSubmit={sumbitHookForm}
                        ></UrineScaleHookForm>
                    </div>
                );
            case "scaleEmo":
                return (
                    <div
                        style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "20px" }}
                        data-testid="glycated-form-component"
                    >
                        <EmoglobinaScaleHookForm
                            selectedClient={selectedClient}
                            onSubmit={sumbitHookForm}
                        ></EmoglobinaScaleHookForm>
                    </div>
                );
            case "scaleUrineEmo":
                return (
                    <div
                        style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "20px" }}
                        data-testid="glycated-form-component"
                    >
                        <UrineEmoScaleHookForm
                            selectedClient={selectedClient}
                            onSubmit={sumbitHookForm}
                        ></UrineEmoScaleHookForm>
                    </div>
                );

            case "scaleFree":
                return (
                    <div
                        style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "20px" }}
                        data-testid="form-component"
                    >
                        {constants.IS_DEBUG && (
                            <DevRiskCaseSelector
                                cases={freeScaleRiskCases}
                                onSelect={(data) => setPrefillData(data)}
                            />
                        )}
                        <FreeScaleHookForm
                            selectedClient={selectedClient}
                            onSubmit={sumbitHookForm}
                            user={user ?? undefined}
                            prefillData={prefillData}
                        ></FreeScaleHookForm>
                    </div>
                );
            default:
                return false;
        }
    };

    const sumbitHookForm = async (data: FormDataLopre | FormDataGlycated | FormDataFree | FormDataUrine | FormDataUrineEmo) => {
        try {
            const body = returnTypeScaleSubmit(data);

            if (body && body.scale.scaleIndexes) {
                setScaleIndexes(body.scale.scaleIndexes);
            }

            const response = await sendData(body);

            setReportId(response.id);
            setreportPrivateKey(response.privateKey);

            switchVisibilityDialog("firstResult");
        } catch (e) {
            setError(e.message ? e.message : "");
            switchVisibilityDialog("errorDialog");
        }
    };

    return (
        <motion.div
            style={{
                backgroundColor: palette.secondary.main,
                height: isXs ? "auto" : "100%",
                minHeight: "100vh",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
        >
            <Container fixed disableGutters={isXs} style={{ height: "auto" }}>
                <Box
                    sx={{
                        ...(isXs && { height: "25%" }),
                        display: "flex",

                        flexDirection: "column",
                    }}
                >
                    <header>
                        <CustomAppBar
                            title={
                                scaleName === "scaleFree"
                                    ? lang.scales.scaleFree.scaleName
                                    : lang.scales.lopreHolder.scaleName
                            }
                            contentColor={palette.secondary.dark}
                            action={
                                scaleName === "scaleFree" ? (
                                    <HelpButton
                                        label={lang.pages.tutorial.tutorialHelpLabel}
                                        color={palette.secondary.dark}
                                        pulse={!isTutorialSeen}
                                        onClick={openTutorial}
                                    />
                                ) : undefined
                            }
                            backButton={
                                <BackButton
                                    sx={{ marginRight: "10px", color: palette.secondary.dark }}
                                    onClick={() => {
                                        setSelectedScale(null);
                                        navigate(-1);
                                        window.scrollTo(0, 0);
                                    }}
                                />
                            }
                        />
                    </header>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "1rem",
                            flexDirection: "column-reverse",
                            ...(isXs && { height: "11rem" }),
                        }}
                    >
                        <img
                            src={"/athletic.png"}
                            alt=""
                            style={{
                                ...(!isXs && { width: isLg ? "25%" : "35%" }),
                                ...(isXs && { height: "100%" }),
                                objectFit: "contain",
                            }}
                        />
                    </Box>
                </Box>
                {returnFormComponet()}

                <FirstResultDialog
                    isOpen={openDialogs.firstResult}
                    scaleName={scaleName ?? ""}
                    reportId={reportId}
                    scaleIndexes={scaleIndexes}
                    reportPrivateKey={reportPrivateKey}
                    closeDialog={() => switchVisibilityDialog("firstResult")}
                />

                <TutorialDialog
                    isOpen={openDialogs.tutorial}
                    closeDialog={() => switchVisibilityDialog("tutorial")}
                />

                <ErrorDialog
                    isOpen={openDialogs.errorDialog}
                    closeDialog={() => switchVisibilityDialog("errorDialog")}
                    test={error}
                ></ErrorDialog>
            </Container>
        </motion.div>
    );
}
